import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import type { OrderFormData } from "@/components/orders/orderFormTypes";

/**
 * "Pending BOL" eligibility for the Create / Edit order forms.
 *
 * The checkbox only exists for multi-leg orders where at least ONE leg's
 * assigned trailer is physically empty (`trailers.load_status = 'empty'`).
 * Fails closed: unassigned trailers, unknown load statuses, and single-leg
 * orders are never eligible.
 *
 * `orders.trailer_id` / `legs.trailer_id` store the trailer *number* (text),
 * so the lookup joins `trailers.trailer_id`, not `trailers.id`.
 */
export function trailerNumbersForForm(fd: Partial<OrderFormData> | null | undefined): string[] {
  if (!fd || fd.order_type !== "multi-leg") return [];
  const sameAssets = fd.use_same_assets_for_all_legs !== false;
  const numbers = sameAssets
    ? [fd.trailer_id]
    : (fd.legs ?? []).map((l) => l.trailer_id || fd.trailer_id);
  return Array.from(
    new Set(numbers.map((n) => (n ?? "").trim()).filter((n) => n !== "")),
  );
}

/** Physical `trailers.load_status` for a set of trailer numbers. */
export async function fetchTrailerLoadStatuses(
  trailerNumbers: string[],
  tenantId?: string | null,
): Promise<(string | null)[]> {
  if (trailerNumbers.length === 0) return [];
  let q = supabase.from("trailers").select("load_status").in("trailer_id", trailerNumbers);
  if (tenantId) q = q.eq("tenant_id", tenantId);
  const { data, error } = await q;
  if (error) throw error;
  return ((data ?? []) as { load_status: string | null }[]).map((r) => r.load_status);
}

export function usePendingBolEligibility(formData: Partial<OrderFormData> | null | undefined) {
  const { tenantId, tenantReady } = useTenant();
  const trailerNumbers = trailerNumbersForForm(formData);
  const key = trailerNumbers.slice().sort().join(",");

  const { data } = useQuery({
    queryKey: ["pending-bol-eligibility", tenantId, key],
    enabled: tenantReady && !!tenantId && trailerNumbers.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trailers")
        .select("trailer_id, load_status")
        .eq("tenant_id", tenantId!)
        .in("trailer_id", trailerNumbers);
      if (error) throw error;
      return (data ?? []) as { trailer_id: string; load_status: string | null }[];
    },
  });

  const anyEmpty = (data ?? []).some((t) => t.load_status === "empty");
  return { eligible: trailerNumbers.length > 0 && anyEmpty, trailerNumbers };
}
